/**
 * useGetActivePlanByEvent Hook
 * Custom React Query hook for fetching the active subscription plan for a specific event
 */

import {useQuery} from 'react-query';
import {ActiveSubscription} from '~types/subscription/subscription.types';
import {useUserStore} from '~providers/userStore/zustand';
import {BASE_API_URL} from '@env';

/**
 * Fetch active subscription plan for a specific event from the API
 */
async function fetchActivePlanByEvent(eventId: string, token?: string): Promise<ActiveSubscription> {
  const headers: Record<string, string> = {
    'accept': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `${token}`;
  }

  const response = await fetch(
    `${BASE_API_URL}stripe/subscription/get-active-plan?event_id=${eventId}`,
    {
      method: 'GET',
      headers,
    }
  );

  if (!response.ok) {
    // If 404 or other error, likely no active subscription
    if (response.status === 404) {
      throw new Error('No active subscription found for this event');
    }
    throw new Error(`Failed to fetch active plan: ${response.status}`);
  }

  const data: ActiveSubscription = await response.json();
  return data;
}

export const useGetActivePlanByEvent = (eventId?: string, enabled: boolean = true) => {
  return useQuery<ActiveSubscription, Error>(
    ['activePlanByEvent', eventId],
    async () => {
      if (!eventId) {
        throw new Error('Event ID is required');
      }
      const token = useUserStore.getState().token;
      return fetchActivePlanByEvent(eventId, token);
    },
    {
      // Enable/disable the query - also disable if eventId is not provided
      enabled: enabled && !!eventId,
      // Cache active plan for 5 minutes
      cacheTime: 1000 * 60 * 5,
      // Consider data fresh for 2 minutes
      staleTime: 1000 * 60 * 2,
      // Don't refetch when window regains focus
      refetchOnWindowFocus: false,
      // Don't refetch on mount if data is still fresh
      refetchOnMount: false,
      // Refetch when network reconnects
      refetchOnReconnect: true,
      // Retry once on failure
      retry: 1,
      // Don't show error in console for 404s (no active subscription)
      onError: (error) => {
        if (error.message.includes('404')) {
          console.debug('No active subscription found for this event');
        }
      },
    },
  );
};
